import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import PaidIcon from '@mui/icons-material/Paid';
import HistoryIcon from '@mui/icons-material/History';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';

import paths from '@src/paths';
import { useMessage } from '@src/context/common';
import {
  downloadInvoicePdf,
  markInvoicePaid,
  retrieveInvoice,
  sendInvoice,
} from '@src/api/invoices';
import { DEFAULT_ACH_INSTRUCTIONS } from '@src/constants/invoicing';

const statusColor = {
  draft: 'default',
  sent: 'info',
  outstanding: 'warning',
  paid: 'success',
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return String(value);
  }
  return amount.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

const formatDateTime = (value) => (value ? dayjs(value).format('MMM D, YYYY h:mm A') : '—');
const formatDate = (value) => (value ? dayjs(value).format('MMM D, YYYY') : '—');

export default function InvoiceDetailPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const message = useMessage();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [sendMessage, setSendMessage] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const loadInvoice = useCallback(() => {
    if (!invoiceId) {
      return;
    }
    setLoading(true);
    setError('');
    retrieveInvoice(invoiceId)
      .then((res) => {
        setInvoice(res.data);
      })
      .catch((err) => {
        console.error('Failed to load invoice', err);
        setError('Unable to load invoice details.');
      })
      .finally(() => setLoading(false));
  }, [invoiceId]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  const handleSend = () => {
    if (!invoiceId) {
      return;
    }
    setSending(true);
    sendInvoice(invoiceId, { message: sendMessage })
      .then((res) => {
        setInvoice(res.data);
        message.success('Invoice sent. Emails dispatched and payment link prepared.');
      })
      .catch((err) => {
        console.error('Failed to send invoice', err);
        message.error('Unable to send the invoice. Please confirm billing details and attachments.');
      })
      .finally(() => setSending(false));
  };

  const handleMarkPaid = () => {
    if (!invoiceId) {
      return;
    }
    setMarkingPaid(true);
    markInvoicePaid(invoiceId, {})
      .then((res) => {
        setInvoice(res.data);
        message.success('Invoice marked as paid.');
      })
      .catch((err) => {
        console.error('Failed to mark invoice paid', err);
        message.error('Unable to mark this invoice as paid.');
      })
      .finally(() => setMarkingPaid(false));
  };

  const handleDownloadPdf = useCallback(() => {
    if (!invoiceId) {
      return;
    }
    setDownloadingPdf(true);
    downloadInvoicePdf(invoiceId)
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const filename = `${invoice?.number || `invoice-${invoiceId}`}.pdf`;
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch((err) => {
        console.error('Failed to download invoice PDF', err);
        message.error('Unable to download the invoice PDF.');
      })
      .finally(() => setDownloadingPdf(false));
  }, [invoiceId, invoice, message]);

  const statusChip = useMemo(() => {
    if (!invoice) {
      return null;
    }
    return (
      <Chip
        label={invoice.status?.toUpperCase() || 'UNKNOWN'}
        color={statusColor[invoice.status] || 'default'}
        size="small"
      />
    );
  }, [invoice]);

  const amountAlerts = useMemo(() => {
    if (!invoice) {
      return null;
    }
    const alerts = [];
    if (invoice.needs_amount_confirmation) {
      alerts.push(
        <Alert key="needs-amount" severity="warning" icon={<InfoIcon fontSize="inherit" />}>
          This invoice has not been amount-confirmed. Review spend and manual adjustments before sending.
        </Alert>
      );
    }
    if (!invoice.line_items?.length) {
      alerts.push(
        <Alert key="missing-line-items" severity="warning" icon={<InfoIcon fontSize="inherit" />}>
          No line items are associated with this invoice. Add at least one before sending.
        </Alert>
      );
    }
    return alerts;
  }, [invoice]);

  const statusNotifications = useMemo(() => {
    if (!invoice) {
      return null;
    }
    const notices = [];
    if (invoice.sent_at) {
      notices.push(
        <Alert key="sent" severity="success" icon={<SendIcon fontSize="inherit" />}>
          Invoice emailed on {formatDateTime(invoice.sent_at)}.
        </Alert>
      );
    }
    if (invoice.outstanding_at) {
      notices.push(
        <Alert key="outstanding" severity="warning" icon={<InfoIcon fontSize="inherit" />}>
          Marked outstanding on {formatDateTime(invoice.outstanding_at)}.
        </Alert>
      );
    }
    if (invoice.paid_at) {
      notices.push(
        <Alert key="paid" severity="success" icon={<PaidIcon fontSize="inherit" />}>
          Payment confirmed on {formatDateTime(invoice.paid_at)}.
        </Alert>
      );
    }
    return notices;
  }, [invoice]);

  if (loading) {
    return (
      <Box p={3} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Stack spacing={2}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            Back
          </Button>
          <Alert severity="error">{error}</Alert>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadInvoice}>
            Retry
          </Button>
        </Stack>
      </Box>
    );
  }

  if (!invoice) {
    return null;
  }

  const totalLineItemAmount =
    invoice.line_items?.reduce(
      (sum, item) => sum + (Number(item.amount ?? item.line_total ?? 0) || 0),
      0
    ) || 0;

  return (
    <Box p={3}>
      <Stack spacing={2} mb={3} direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
        <Stack spacing={1}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(paths.dashboard.invoices.list)}>
            Back to invoices
          </Button>
          <Typography variant="h4">{invoice.number || `Invoice #${invoice.id}`}</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {statusChip}
            <Typography color="text.secondary">Total: {formatCurrency(invoice.total_amount)}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Issued {formatDate(invoice.issue_date)} · Due {formatDate(invoice.due_date)}
          </Typography>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} width={{ xs: '100%', md: 'auto' }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(paths.dashboard.invoices.edit(invoice.id))}
          >
            Edit invoice
          </Button>
          <LoadingButton
            variant="outlined"
            startIcon={<DownloadIcon />}
            loading={downloadingPdf}
            onClick={handleDownloadPdf}
          >
            Download PDF
          </LoadingButton>
          <LoadingButton
            variant="contained"
            color="primary"
            startIcon={<SendIcon />}
            loading={sending}
            onClick={handleSend}
            disabled={sending || markingPaid}
          >
            Send invoice
          </LoadingButton>
          <LoadingButton
            variant="contained"
            color="success"
            startIcon={<PaidIcon />}
            loading={markingPaid}
            onClick={handleMarkPaid}
            disabled={markingPaid || invoice.status === 'paid'}
          >
            Mark paid
          </LoadingButton>
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardHeader title="Invoice summary" />
            <Divider />
            <CardContent>
              <Stack spacing={2}>
                {statusNotifications}
                {amountAlerts}
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Client</Typography>
                  <Typography variant="body2">{invoice.client_name}</Typography>
                </Stack>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Billing window</Typography>
                  <Typography variant="body2">
                    {formatDate(invoice.period_start)} – {formatDate(invoice.period_end)}
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Billing contacts</Typography>
                  {invoice.billing_contacts_snapshot?.length ? (
                    <Stack spacing={0.5}>
                      {invoice.billing_contacts_snapshot.map((contact, index) => {
                        const name = contact?.name;
                        const email = contact?.email || contact?.address || '—';
                        return (
                          <Typography variant="body2" key={`${email}-${index}`}>
                            {name ? `${name} • ` : ''}{email}
                          </Typography>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Typography variant="body2">No billing contacts on file.</Typography>
                  )}
                </Stack>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Billing address</Typography>
                  {invoice.billing_address_snapshot && Object.keys(invoice.billing_address_snapshot).length ? (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {[
                        invoice.billing_address_snapshot?.line1,
                        invoice.billing_address_snapshot?.line2,
                        [
                          invoice.billing_address_snapshot?.city,
                          invoice.billing_address_snapshot?.state,
                          invoice.billing_address_snapshot?.postal_code,
                        ]
                          .filter(Boolean)
                          .join(', '),
                        invoice.billing_address_snapshot?.country,
                      ]
                        .filter(Boolean)
                        .join('\n')}
                    </Typography>
                  ) : (
                    <Typography variant="body2">No billing address captured.</Typography>
                  )}
                </Stack>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Line items total</Typography>
                  <Typography variant="body2">{formatCurrency(totalLineItemAmount)}</Typography>
                </Stack>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Client message</Typography>
                  <Typography variant="body2">{invoice.external_notes || '—'}</Typography>
                </Stack>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Internal notes</Typography>
                  <Typography variant="body2">{invoice.notes || '—'}</Typography>
                </Stack>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">ACH instructions</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {invoice.ach_instructions_snapshot || DEFAULT_ACH_INSTRUCTIONS}
                  </Typography>
                </Stack>
                {invoice.stripe_payment_link && (
                  <Alert severity="info">
                    Stripe payment link generated:{' '}
                    <Link href={invoice.stripe_payment_link} target="_blank" rel="noopener noreferrer">
                      {invoice.stripe_payment_link}
                    </Link>
                  </Alert>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card>
            <CardHeader title="Send options" />
            <Divider />
            <CardContent>
              <Stack spacing={2}>
                <TextField
                  label="Message to include"
                  multiline
                  minRows={3}
                  value={sendMessage}
                  onChange={(event) => setSendMessage(event.target.value)}
                  placeholder="Optional note to include in the invoice email."
                />
                <Alert severity="info">
                  Sending the invoice will attach the PDF, include uploaded attachments, generate a Stripe payment link, and
                  deliver the email to billing contacts.
                </Alert>
                <Button
                  variant="outlined"
                  startIcon={<HistoryIcon />}
                  onClick={() => {
                    const historyEl = document.getElementById('invoice-history');
                    if (historyEl) {
                      historyEl.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  Jump to history
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} mt={1}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardHeader title="Line items" />
            <Divider />
            <CardContent>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.line_items?.length ? (
                    invoice.line_items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell align="right">{formatCurrency(item.amount ?? item.line_total)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        <Typography color="text.secondary">No line items found.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card>
            <CardHeader title="Attachments" />
            <Divider />
            <CardContent>
              {invoice.attachments?.length ? (
                <List dense>
                  {invoice.attachments.map((attachment) => (
                    <ListItem key={attachment.id} secondaryAction={
                      <IconButton
                        edge="end"
                        component="a"
                        href={attachment.file}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <DownloadIcon />
                      </IconButton>
                    }>
                      <ListItemIcon>
                        <AttachFileIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={attachment.original_filename || 'Attachment'}
                        secondary={`Uploaded ${formatDateTime(attachment.uploaded_at)}${attachment.notes ? ` • ${attachment.notes}` : ''}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="info">No attachments uploaded yet.</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box mt={3} id="invoice-history">
        <Card>
          <CardHeader title="Status history" />
          <Divider />
          <CardContent>
            {invoice.status_history?.length ? (
              <List>
                {invoice.status_history.map((entry) => (
                  <ListItem key={entry.id}>
                    <ListItemIcon>
                      <HistoryIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${entry.from_status?.toUpperCase()} → ${entry.to_status?.toUpperCase()}`}
                      secondary={`${formatDateTime(entry.changed_at)}${entry.changed_by_name ? ` • ${entry.changed_by_name}` : ''}${entry.notes ? ` • ${entry.notes}` : ''}`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Alert severity="info">No status transitions recorded.</Alert>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
